/*
  NOÈVA — noeva-carrinho.js
  A parte viva da gaveta do carrinho (snippets/noeva-carrinho*.liquid).

  O que faz:
  - Pede o miolo da gaveta a /cart?view=nv de cada vez que o carrinho muda e
    troca-o. Não desenha nada à mão: o HTML e os preços vêm do Liquid.
  - Quantidades, remover, trocar a cor da prenda, adicionar sugestões, o
    extra do interruptor — tudo via /cart/*.js da Shopify.
  - Prendas por escalão: depois de cada render vê o total e põe/tira as
    prendas que faltam/sobram. Se uma prenda vier com preço (o desconto
    automático não pegou), tira-a e não volta a pôr nesta visita — nunca se
    cobra uma prenda.
  - Quando só ficam extras no carrinho (prendas e o extra do interruptor),
    limpa tudo: ninguém quer uma encomenda só com a prenda.
  - Temporizador «reservado por»: reinicia quando entra um produto; ao chegar
    a zero fica a zeros e não mexe no carrinho.
  - Apanha adições feitas por outros scripts (o tema, a app de
    personalização) e actualiza a gaveta na mesma.

  Fala com o theme.js do Motion só em três sítios: o evento ajaxProduct:added,
  o evento cart:open, e o buildCart do theme.CartForm, que aqui passa a pedir
  o nosso HTML em vez do do tema.
*/
(function () {
  'use strict';

  var raiz = document.querySelector('[data-nv-cart]');
  if (!raiz) return;

  // A gaveta é renderizada dentro do cabeçalho do tema, que tem transform;
  // um position: fixed lá dentro fica preso ao cabeçalho e, no iPhone,
  // quando o teclado abre e o Safari faz scroll, a página aparece por baixo
  // da gaveta. No <body> o fixed é mesmo relativo ao ecrã. Os listeners do
  // tema (theme.Drawers) guardam o elemento, não o sítio — continuam a valer.
  if (raiz.parentElement !== document.body) {
    document.body.appendChild(raiz);
  }

  var conteudo = raiz.querySelector('[data-nv-cart-conteudo]');
  var TIMER_CHAVE = 'nv_cart_timer_fim';
  var MAX_CICLOS = 4;

  var dados = lerDados();
  var ocupado = false;
  var pendente = false;
  var ciclos = 0;
  var prendasBloqueadas = false;
  var sugIdx = 0;
  var timerId = null;

  /* ---------- utilidades ---------- */

  function lerDados() {
    var el = raiz.querySelector('[data-nv-cart-dados]');
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return { escaloes: [], extra: null, prendasLigadas: false, timerMinutos: 10 };
    }
  }

  function miolo() {
    return raiz.querySelector('[data-nv-cart-miolo]');
  }

  function pedir(url, opts) {
    opts = opts || {};
    opts.credentials = 'same-origin';
    opts.headers = Object.assign(
      { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-NV-Cart': '1' },
      opts.headers || {}
    );
    if (opts.json) {
      opts.method = 'POST';
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(opts.json);
      delete opts.json;
    }
    return fetch(url, opts).then(function (r) {
      if (!r.ok) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          var err = new Error(j.description || j.message || 'Erro no carrinho');
          err.detalhe = j;
          throw err;
        });
      }
      return r;
    });
  }

  function mudar(key, qty) {
    return pedir('/cart/change.js', { json: { id: key, quantity: qty } });
  }

  function adicionar(variante, props) {
    return pedir('/cart/add.js', { json: { items: [{ id: variante, quantity: 1, properties: props || {} }] } });
  }

  // As chaves das linhas podem mudar entre o HTML e o estado real (o
  // preview troca de carrinho, apps mexem por trás). Para tirar uma coisa
  // do carrinho vai-se buscar a chave fresca a /cart.js pelo produto.
  function tirarProduto(produtoId) {
    return pedir('/cart.js?t=' + Date.now()).then(function (r) { return r.json(); }).then(function (c) {
      var linhas = c.items.filter(function (i) { return i.product_id === produtoId; });
      return sequencia(linhas.map(function (i) {
        return function () { return mudar(i.key, 0); };
      }));
    });
  }

  /* ---------- código de desconto ---------- */

  // Os códigos já aplicados vêm nos chips do HTML (cart.discount_applications).
  function codigosAtivos() {
    return Array.prototype.map.call(miolo().querySelectorAll('[data-nv-cart-codigo-tirar]'), function (b) {
      return b.dataset.codigo;
    });
  }

  // A Shopify aceita `discount` em /cart/update.js (vários códigos separados
  // por vírgula; vazio tira todos). A resposta traz discount_codes com
  // `applicable` — é assim que se sabe se o código pegou.
  function definirCodigos(lista) {
    return pedir('/cart/update.js', { json: { discount: lista.join(',') } })
      .then(function (r) { return r.json(); });
  }

  function aplicarCodigo() {
    var input = miolo().querySelector('[data-nv-cart-codigo-input]');
    var btn = miolo().querySelector('[data-nv-cart-codigo-aplicar]');
    var erro = miolo().querySelector('[data-nv-cart-codigo-erro]');
    if (!input) return;
    var codigo = (input.value || '').trim().toUpperCase();
    if (!codigo) { input.focus(); return; }
    if (erro) erro.hidden = true;
    var lista = codigosAtivos().filter(function (c) { return c !== codigo; });
    lista.push(codigo);
    if (btn) btn.disabled = true;
    input.disabled = true;
    definirCodigos(lista)
      .then(function (cart) {
        var entrada = (cart.discount_codes || []).filter(function (d) { return (d.code || '').toUpperCase() === codigo; })[0];
        if (!entrada || entrada.applicable === false) {
          // Não pegou: tira-o outra vez e diz porquê.
          return definirCodigos(codigosAtivos()).then(function () {
            throw new Error(dados.codigoErro || 'Este código não é válido.');
          });
        }
      })
      .then(function () { refrescar(); })
      .catch(function (e) {
        if (btn) btn.disabled = false;
        input.disabled = false;
        if (erro) { erro.textContent = e.message; erro.hidden = false; }
        input.focus();
      });
  }

  function limpar() {
    return pedir('/cart/clear.js', { method: 'POST' });
  }

  function abrir() {
    document.dispatchEvent(new CustomEvent('cart:open'));
  }

  /* ---------- render ---------- */

  function refrescar() {
    if (ocupado) {
      pendente = true;
      return;
    }
    ocupado = true;
    raiz.classList.add('is-loading');
    fetch('/cart?view=nv&t=' + Date.now(), { credentials: 'same-origin', headers: { 'X-NV-Cart': '1' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        conteudo.innerHTML = html;
        dados = lerDados();
        return depoisDeRender();
      })
      .catch(function (e) {
        if (window.console) console.warn('[noeva-carrinho]', e);
      })
      .then(function () {
        ocupado = false;
        raiz.classList.remove('is-loading');
        if (pendente) {
          pendente = false;
          refrescar();
        }
      });
  }

  function depoisDeRender() {
    actualizarContagem();
    iniciarSugestoes();
    iniciarTimer();
    return conciliar().then(function (mexeu) {
      if (mexeu && ciclos < MAX_CICLOS) {
        ciclos++;
        pendente = true;
      } else {
        ciclos = 0;
      }
    });
  }

  /* ---------- prendas e extras: pôr e tirar ---------- */

  function conciliar() {
    var m = miolo();
    if (!m) return Promise.resolve(false);

    var total = parseInt(m.dataset.total, 10) || 0;
    var count = parseInt(m.dataset.count, 10) || 0;
    var normais = parseInt(m.dataset.normais, 10) || 0;
    var extras = parseInt(m.dataset.extras, 10) || 0;
    var prendaPaga = m.dataset.prendaPaga === 'true';

    // Só sobraram extras: limpa tudo.
    if (count > 0 && normais === 0 && extras > 0) {
      return limpar().then(function () { return true; }).catch(function () { return false; });
    }

    var prendasNoCarrinho = Array.prototype.slice.call(m.querySelectorAll('[data-prenda]'));

    // Uma prenda com preço é um desconto que não pegou. Sai, e não volta.
    if (prendaPaga) {
      prendasBloqueadas = true;
      return sequencia(dados.escaloes.map(function (esc) {
        return function () { return tirarProduto(esc.produto); };
      })).then(function (n) { return n > 0; });
    }

    if (!dados.prendasLigadas || prendasBloqueadas || !dados.escaloes.length) {
      return Promise.resolve(false);
    }

    var passos = [];
    dados.escaloes.forEach(function (esc) {
      var presente = prendasNoCarrinho.filter(function (li) {
        return parseInt(li.dataset.produto, 10) === esc.produto;
      });
      var deve = normais > 0 && total >= esc.min;
      if (deve && presente.length === 0) {
        passos.push(function () { return adicionar(esc.variante, { _nv_prenda: '1' }); });
      } else if (!deve && presente.length) {
        passos.push(function () { return tirarProduto(esc.produto); });
      } else if (deve && presente.length) {
        // Uma prenda é uma. Se por acaso houver mais, volta a uma.
        presente.forEach(function (li) {
          if (parseInt(li.dataset.qty, 10) > 1) {
            passos.push(function () { return mudar(li.dataset.key, 1); });
          }
        });
      }
    });

    return sequencia(passos).then(function (n) { return n > 0; });
  }

  // Corre os pedidos um a seguir ao outro (a Shopify não gosta de vários ao
  // mesmo tempo no carrinho). Devolve quantos correram.
  function sequencia(fns) {
    var n = 0;
    return fns.reduce(function (p, fn) {
      return p.then(function () {
        return fn().then(function () { n++; }).catch(function () {});
      });
    }, Promise.resolve()).then(function () { return n; });
  }

  /* ---------- contagem no cabeçalho ---------- */

  function actualizarContagem() {
    var m = miolo();
    var n = m ? parseInt(m.dataset.count, 10) || 0 : 0;

    var no = raiz.querySelector('[data-nv-cart-n]');
    if (no) no.textContent = n;

    document.querySelectorAll('[data-nv-carrinho-n]').forEach(function (el) {
      el.textContent = '(' + n + ')';
    });
    document.querySelectorAll('.cart-link__bubble-num').forEach(function (el) {
      el.textContent = n;
    });
    document.querySelectorAll('.cart-link__bubble').forEach(function (el) {
      el.classList.toggle('cart-link__bubble--visible', n > 0);
    });
    document.querySelectorAll('.nv-cab__carrinho-icone').forEach(function (icone) {
      var bolha = icone.querySelector('.nv-cab__bolha');
      if (n > 0) {
        if (!bolha) {
          bolha = document.createElement('i');
          bolha.className = 'nv-cab__bolha';
          icone.appendChild(bolha);
        }
        bolha.textContent = n;
      } else if (bolha) {
        bolha.remove();
      }
    });
  }

  /* ---------- «Completa com»: um de cada vez ---------- */

  function iniciarSugestoes(direccao) {
    var lista = raiz.querySelectorAll('[data-nv-cart-sug]');
    if (!lista.length) return;
    if (sugIdx >= lista.length) sugIdx = 0;
    lista.forEach(function (li, i) {
      var activa = i === sugIdx;
      li.classList.remove('nv-cart__sug--entra-dir', 'nv-cart__sug--entra-esq');
      if (activa && direccao) {
        // Desliza: entra pela direita ao avançar, pela esquerda ao recuar.
        void li.offsetWidth;
        li.classList.add(direccao > 0 ? 'nv-cart__sug--entra-dir' : 'nv-cart__sug--entra-esq');
      }
      li.classList.toggle('is-active', activa);
    });
    var pos = raiz.querySelector('[data-nv-cart-sug-pos]');
    if (pos) pos.textContent = (sugIdx + 1) + '/' + lista.length;
    var nav = raiz.querySelector('.nv-cart__sug-nav');
    if (nav) nav.hidden = lista.length < 2;
  }

  function rodarSugestoes(passo) {
    var lista = raiz.querySelectorAll('[data-nv-cart-sug]');
    if (!lista.length) return;
    sugIdx = (sugIdx + passo + lista.length) % lista.length;
    iniciarSugestoes(passo);
  }

  // Passa sozinho para a sugestão seguinte de 10 em 10 s, só com a gaveta
  // aberta. Sempre que a cliente mexe (setas, cor, adicionar), o relógio
  // recomeça do zero — só volta a andar sozinho 10 s depois do último toque.
  var SUG_INTERVALO = 10000;
  var sugTimer = null;

  function agendarSugestoes() {
    if (sugTimer) clearTimeout(sugTimer);
    sugTimer = setTimeout(function () {
      sugTimer = null;
      var aberta = raiz.classList.contains('drawer--is-open');
      var lista = raiz.querySelectorAll('[data-nv-cart-sug]');
      if (aberta && lista.length > 1 && !document.hidden) rodarSugestoes(1);
      agendarSugestoes();
    }, SUG_INTERVALO);
  }

  raiz.addEventListener('pointerdown', function (evt) {
    if (evt.target.closest('[data-nv-cart-sugestoes]')) agendarSugestoes();
  });
  document.addEventListener('drawerOpen.CartDrawer', agendarSugestoes);
  agendarSugestoes();

  /* ---------- temporizador ---------- */

  function timerFim() {
    return parseInt(localStorage.getItem(TIMER_CHAVE), 10) || 0;
  }

  function reiniciarTimer() {
    var min = parseInt(dados.timerMinutos, 10) || 10;
    try { localStorage.setItem(TIMER_CHAVE, String(Date.now() + min * 60000)); } catch (e) {}
  }

  function iniciarTimer() {
    if (timerId) { clearInterval(timerId); timerId = null; }
    var alvo = raiz.querySelector('[data-nv-cart-timer-valor]');
    if (!alvo) return;
    // Sem contagem, ou uma que já acabou noutra visita: começa de novo.
    if (timerFim() <= Date.now()) reiniciarTimer();
    tick();
    timerId = setInterval(tick, 1000);
  }

  function tick() {
    var alvo = raiz.querySelector('[data-nv-cart-timer-valor]');
    if (!alvo) { clearInterval(timerId); timerId = null; return; }
    var resto = Math.max(0, timerFim() - Date.now());
    var mm = Math.floor(resto / 60000);
    var ss = Math.floor((resto % 60000) / 1000);
    alvo.textContent = (mm < 10 ? '0' : '') + mm + ':' + (ss < 10 ? '0' : '') + ss;
  }

  /* ---------- interacções ---------- */

  function comLoading(el, promessa) {
    if (el) el.disabled = true;
    return promessa.catch(function (e) {
      if (window.console) console.warn('[noeva-carrinho]', e);
      if (e && e.message) mostrarErro(e.message);
    }).then(function () {
      if (el) el.disabled = false;
      refrescar();
    });
  }

  function mostrarErro(msg) {
    var m = miolo();
    if (!m) return;
    var velho = m.querySelector('.nv-cart__erro');
    if (velho) velho.remove();
    var p = document.createElement('p');
    p.className = 'nv-cart__erro';
    p.setAttribute('role', 'alert');
    p.textContent = msg;
    p.style.cssText = 'margin:8px 20px;padding:10px 12px;font-size:13px;line-height:1.4;color:#4c1c24;background:#fdf5f6;border-left:2px solid #4c1c24;border-radius:4px';
    var corpo = m.querySelector('[data-nv-cart-corpo]');
    (corpo || m).prepend(p);
  }

  raiz.addEventListener('click', function (evt) {
    var t = evt.target;

    var mais = t.closest('[data-nv-cart-mais]');
    var menos = t.closest('[data-nv-cart-menos]');
    if (mais || menos) {
      var caixa = t.closest('[data-nv-cart-qty]');
      var num = caixa.querySelector('[data-nv-cart-qty-num]');
      var q = parseInt(num.value, 10) || 0;
      q = mais ? q + 1 : Math.max(0, q - 1);
      num.value = q;
      if (mais) reiniciarTimer();
      comLoading(mais || menos, mudar(caixa.dataset.key, q));
      return;
    }

    var rem = t.closest('[data-nv-cart-remover]');
    if (rem) {
      comLoading(rem, mudar(rem.dataset.key, 0));
      return;
    }

    var add = t.closest('[data-nv-cart-sug-add]');
    if (add) {
      reiniciarTimer();
      // A propriedade _nv_sugestao é o que faz a Function dar o desconto do
      // 2.º artigo. Vem no HTML porque a percentagem é uma definição do tema.
      var props = {};
      try { props = JSON.parse(add.dataset.props || '{}'); } catch (e) {}
      comLoading(add, adicionar(parseInt(add.dataset.variante, 10), props));
      return;
    }

    // Código de desconto: aplicar / tirar.
    if (t.closest('[data-nv-cart-codigo-aplicar]')) {
      aplicarCodigo();
      return;
    }
    var tirar = t.closest('[data-nv-cart-codigo-tirar]');
    if (tirar) {
      comLoading(tirar, definirCodigos(codigosAtivos().filter(function (c) { return c !== tirar.dataset.codigo; })));
      return;
    }

    if (t.closest('[data-nv-cart-sug-ant]')) { rodarSugestoes(-1); return; }
    if (t.closest('[data-nv-cart-sug-seg]')) { rodarSugestoes(1); return; }
  });

  raiz.addEventListener('change', function (evt) {
    var t = evt.target;

    if (t.matches('[data-nv-cart-qty-num]')) {
      var caixa = t.closest('[data-nv-cart-qty]');
      var q = Math.max(0, parseInt(t.value, 10) || 0);
      comLoading(null, mudar(caixa.dataset.key, q));
      return;
    }

    // Trocar a cor da prenda: sai a variante velha, entra a nova.
    if (t.matches('[data-nv-cart-troca-variante]')) {
      var key = t.dataset.key;
      var nova = parseInt(t.value, 10);
      comLoading(t, mudar(key, 0).then(function () {
        return adicionar(nova, { _nv_prenda: '1' });
      }));
      return;
    }

    // Cor na sugestão: só actualiza o botão e o preço; ainda não adiciona.
    if (t.matches('[data-nv-cart-sug-variante]')) {
      var sug = t.closest('[data-nv-cart-sug]');
      var opt = t.options[t.selectedIndex];
      sug.querySelector('[data-nv-cart-sug-add]').dataset.variante = opt.value;
      var preco = sug.querySelector('[data-nv-cart-sug-preco]');
      var riscado = sug.querySelector('[data-nv-cart-sug-riscado]');
      if (preco && opt.dataset.preco) preco.textContent = opt.dataset.preco;
      if (riscado) {
        if (opt.dataset.riscado) { riscado.textContent = opt.dataset.riscado; riscado.hidden = false; }
        else riscado.hidden = true;
      }
      return;
    }

    // O extra do interruptor.
    if (t.matches('[data-nv-cart-extra]')) {
      var variante = parseInt(t.dataset.variante, 10);
      if (t.checked) {
        comLoading(t, adicionar(variante));
      } else {
        var produto = parseInt(t.dataset.produto, 10) || (dados.extra && dados.extra.produto);
        comLoading(t, produto ? tirarProduto(produto) : Promise.resolve());
      }
      return;
    }
  });

  // Enter na quantidade não deve submeter nada.
  raiz.addEventListener('keydown', function (evt) {
    if (evt.key === 'Enter' && evt.target.matches('[data-nv-cart-codigo-input]')) {
      evt.preventDefault();
      aplicarCodigo();
      return;
    }
    if (evt.key === 'Enter' && evt.target.matches('[data-nv-cart-qty-num]')) {
      evt.preventDefault();
      evt.target.blur();
    }
  });

  /* ---------- ligação ao tema e a outros scripts ---------- */

  // O formulário «adicionar ao carrinho» das páginas de produto. Neste tema
  // o JavaScript da secção de produto não corre (era a app Kaching que
  // apanhava o submit), por isso sem isto o botão levava para /cart. Se
  // outro script já tratou do submit (defaultPrevented), fica-se quieto.
  document.addEventListener('submit', function (evt) {
    var f = evt.target;
    if (!f || f.tagName !== 'FORM' || evt.defaultPrevented) return;
    if (!/\/cart\/add/.test(f.getAttribute('action') || '')) return;
    if (f.closest('[data-nv-cart]')) return;
    evt.preventDefault();

    var botao = evt.submitter || f.querySelector('[name="add"], .add-to-cart, [type="submit"]');
    if (botao) { botao.classList.add('btn--loading'); botao.disabled = true; }

    pedir('/cart/add.js', { method: 'POST', body: new FormData(f) })
      .then(function () {
        reiniciarTimer();
        abrir();
        refrescar();
        document.dispatchEvent(new CustomEvent('nv-cart:adicionado'));
      })
      .catch(function (e) {
        window.alert(e && e.message ? e.message : 'Não foi possível adicionar ao carrinho.');
      })
      .then(function () {
        if (botao) { botao.classList.remove('btn--loading'); botao.disabled = false; }
      });
  });

  // O tema avisa quando o formulário do produto adiciona ao carrinho.
  document.addEventListener('ajaxProduct:added', function () {
    reiniciarTimer();
    refrescar();
  });
  document.addEventListener('cart:build', refrescar);

  // O theme.CartForm do Motion vai buscar o HTML dele a /cart?view=ajax.
  // A partir daqui vai buscar o nosso. (Corre depois do theme.js, que é
  // deferido e vem mais abaixo na página.)
  document.addEventListener('DOMContentLoaded', function () {
    if (window.theme && theme.CartForm && theme.CartForm.prototype) {
      theme.CartForm.prototype.buildCart = function () { refrescar(); };
    }
  });

  // Outros scripts (app de personalização, botão fixo) falam com o carrinho
  // directamente. Ouve-se o fetch e o XHR: se mexeram no carrinho, a gaveta
  // actualiza-se; se adicionaram, abre.
  var RE_CART = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;

  function apanhar(url, nosso, ok) {
    if (nosso || !ok) return;
    var m = RE_CART.exec(url || '');
    if (!m) return;
    if (m[1] === 'add') {
      reiniciarTimer();
      abrir();
    }
    refrescar();
  }

  var fetchOriginal = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var h = (init && init.headers) || (input && input.headers) || {};
    var nosso = h['X-NV-Cart'] === '1' || (typeof h.get === 'function' && h.get('X-NV-Cart') === '1');
    return fetchOriginal.apply(this, arguments).then(function (resp) {
      try { apanhar(url, nosso, resp.ok); } catch (e) {}
      return resp;
    });
  };

  var xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (RE_CART.test(url || '')) {
      this.addEventListener('load', function () {
        try { apanhar(url, false, this.status >= 200 && this.status < 300); } catch (e) {}
      });
    }
    return xhrOpen.apply(this, arguments);
  };

  // Arranque: o HTML já veio na página; só falta o que é vivo.
  depoisDeRender().then(function () {
    if (pendente) { pendente = false; refrescar(); }
  });
})();
